import { Box, Grid, Menu, MenuItem, Stack } from '@mui/material';

import { MutableRefObject, useEffect } from 'react';

import { useSelector } from 'react-redux';

import { ReactComponent as CardIcon } from 'common/assets/images/grid.svg';
import { ReactComponent as HeatMap } from 'common/assets/images/heat-map.svg';
import { ReactComponent as MoreMenu } from 'common/assets/images/more-menu.svg';
import { ReactComponent as TableIcon } from 'common/assets/images/table.svg';
import { ViewType } from 'common/constants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { ToolbarType } from 'common/constants/TaxonomyConstants';
import { RootState } from 'common/store/AppStore';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import styles from './SectionToolbar.module.scss';

interface ISectionToolbar {
	disabled?: boolean;
	types: ToolbarType[];
	viewType?: string;
	isHeatMapViewVisible: boolean;
	isPrcCardViewEnabled: boolean;
	handleToggleViewType: (type: ViewType) => void;
	moreMenuRef: MutableRefObject<HTMLDivElement> | null;
	handleClickMoreMenu: () => void;
	moreMenuAnchorEl: HTMLElement | null;
	handleCloseMoreMenu: () => void;
	exportHandler: () => Promise<void>;
	initFunc: () => void;
}

const { TABLE, HEATMAP, CARD } = ViewType;
const { IS_EXPORT_BUTTON_ENABLED } = FeatureFlagsKeysEnum;

const SectionToolbar = (props: ISectionToolbar) => {
	const tabIndex = useSelector(
		(state: RootState) => state.common.selectedTabIndex,
	);
	const {
		types,
		viewType,
		disabled,
		isHeatMapViewVisible,
		isPrcCardViewEnabled,
		handleToggleViewType,
		moreMenuRef,
		handleClickMoreMenu,
		moreMenuAnchorEl,
		handleCloseMoreMenu,
		exportHandler,
		initFunc,
	} = props;

	const isLDResponseExists: boolean = useSelector(
		(state: RootState) => state.common.isLDResponseAvailable,
	);
	const isExportButtonEnabled = !useFeatureFlag(
		IS_EXPORT_BUTTON_ENABLED,
		isLDResponseExists,
	);

	const getIconClass = (targetType: ViewType) =>
		[
			styles.icon,
			viewType === targetType ? styles.active : styles.inactive,
		].join(' ');

	const handleClickExport = () => {
		handleCloseMoreMenu();
		exportHandler();
	};

	useEffect(() => {
		initFunc();
	}, []);

	return (
		<>
			<Grid
				item
				alignContent="center"
				className={styles['section-tool-bar-wrapper']}
				xs="auto"
			>
				<Stack
					direction="row"
					gap={1}
					alignItems="center"
					alignSelf="flex-end"
					justifyContent="flex-end"
					data-testid="toolbar-stack"
				>
					{types.includes(ToolbarType.TOGGLE_VIEW_HEATMAP) && !disabled && (
						<div className={styles['toggle-view']}>
							{isPrcCardViewEnabled && (
								<Box
									data-testid="card-btn"
									onClick={() => handleToggleViewType(CARD)}
									className={getIconClass(CARD)}
								>
									<CardIcon />
								</Box>
							)}
							<Box
								data-testid="table-btn"
								onClick={() => handleToggleViewType(TABLE)}
								className={getIconClass(TABLE)}
							>
								<TableIcon />
							</Box>
							{isHeatMapViewVisible && (
								<Box
									data-testid="heatmap-btn"
									onClick={() => handleToggleViewType(HEATMAP)}
									className={getIconClass(HEATMAP)}
								>
									<HeatMap />
								</Box>
							)}
						</div>
					)}
				</Stack>
			</Grid>
			<Grid
				item
				className={styles['more-menu-icon']}
				ref={moreMenuRef}
				onClick={handleClickMoreMenu}
				xs="auto"
				data-testid="more-menu-icon"
			>
				<MoreMenu />
			</Grid>
			<Menu
				anchorEl={moreMenuAnchorEl}
				onClose={handleCloseMoreMenu}
				open={Boolean(moreMenuAnchorEl)}
				slotProps={{
					paper: {
						style: {
							width: '14rem',
							padding: '0 8px',
						},
					},
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			>
				<MenuItem
					value="export"
					key="export"
					onClick={handleClickExport}
					// export button will be disabled in below cases
					// 1. feature flag is false
					// 2. obligation category is seleted
					disabled={!isExportButtonEnabled || tabIndex === 3}
				>
					Export
				</MenuItem>
			</Menu>
		</>
	);
};
export default SectionToolbar;
